import { useEffect, useState } from "react"
import { useRouter, useSearchParamsCompat } from "@/lib/router-helpers"
import { UserRoundPlus, X } from "lucide-react"
import { useSignup } from "@/hooks/use-auth"
import { clearPendingPhone } from "@/lib/auth"
import { useAuth } from "@/components/auth-provider"
import { toast } from "sonner"
import { Toaster } from "@/components/ui/sonner"
import { useTranslation } from "react-i18next"
import TermsAndConditionsPage from "@/app/terms-and-conditions/page"
import TermsAndConditionsModal from "@/components/terms-and-conditions-modal"
import Cookies from "js-cookie"

export default function RegisterPage() {
  const router = useRouter()
  const [searchParams] = useSearchParamsCompat()
  const returnUrl = searchParams?.get("returnUrl")
  const { login, isAuthenticated } = useAuth()
  const [phone, setPhone] = useState<string | null>(null)
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [agree, setAgree] = useState(false)
  const [showTerms, setShowTerms] = useState(false)
  const signupMutation = useSignup()
  const { t } = useTranslation()

  useEffect(() => {
    if (typeof window !== "undefined") {
      const storedPhone = Cookies.get("qetero.verifiedPhone")
      setPhone(storedPhone || null)

      if (!storedPhone) {
        router.push("/auth/verify")
      }
    }
  }, [isAuthenticated, router])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!phone || !firstName.trim() || !lastName.trim()) {
      toast.error(t("Please fill all fields"))
      return
    }
    if (!agree) {
      toast.error(t("You must agree to the Terms and Conditions"))
      return
    }

    try {
      const result = await signupMutation.mutateAsync({
        mobile: phone,
        firstname: firstName.trim(),
        lastname: lastName.trim(),
        password: "12345",
      })

      if (result.message && result.status === "success") {
        login(result.message)
        clearPendingPhone()
        Cookies.remove("qetero.verifiedPhone", { path: '/' })
        toast.success(t("Account created successfully"))
        
        const target = returnUrl ? decodeURIComponent(returnUrl) : `/`
        window.location.href = target
      }
    } catch (error: any) {
      toast.error(error.message || t("Failed to create account. Please try again."))
    }
  }

   if (!phone) return null

  return (
    <>
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-fade-in-up {
          animation: fadeInUp 0.6s ease-out forwards;
        }
        .animate-scale-in {
          animation: scaleIn 0.3s ease-out forwards;
        }
      `}</style>

      {/* Terms Modal */}
      {showTerms && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-300">
          <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full relative p-6 overflow-y-auto max-h-[90vh] animate-scale-in">
            <button
              onClick={() => setShowTerms(false)}
              className="absolute top-4 right-4 p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900 transition-colors"
            >
              <X size={20} />
            </button>
            <TermsAndConditionsModal  onClose={() => setShowTerms(false)}/>
          </div>
        </div>
      )}

      {/* Register Form */}
      <div className="min-h-[80vh] flex items-center justify-center px-4 pb-8 pt-4 md:pt-12">
        <div className="w-full max-w-lg animate-fade-in-up">
          <div className="rounded-3xl bg-white shadow-xl shadow-pink-50/50 border border-gray-100 p-6 md:p-10 relative overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-pink-500 via-rose-500 to-pink-600" />
            <div className="absolute -top-24 -right-24 w-48 h-48 bg-blue-50 rounded-full blur-3xl opacity-60 pointer-events-none" />
            <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-blue-50 rounded-full blur-3xl opacity-60 pointer-events-none" />

            <div className="relative z-10 flex flex-col items-center text-center">
              <div className="w-20 h-20 rounded-full bg-pink-50 flex items-center justify-center shadow-sm mb-6 group transition-transform duration-500 hover:scale-110 hover:rotate-3">
                <UserRoundPlus className="w-9 h-9 text-pink-600 group-hover:text-pink-500 transition-colors" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 tracking-tight">{t("createAnAccount")}</h3>
              <p className="text-sm text-gray-500 mt-2 max-w-xs mx-auto">
                {t("Provide your name to continue.")}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="mt-8 relative z-10 space-y-5">
              <div className="group">
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-2 ml-1">
                  {t("firstName")} <span className="text-pink-500">*</span>
                </label>
                <input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Enter first name"
                  disabled={signupMutation.isPending}
                  className="w-full h-12 rounded-xl border border-gray-200 bg-gray-50/50 px-4 text-gray-900 placeholder-gray-400 transition-all duration-300 focus:bg-white focus:border-pink-500 focus:outline-none focus:ring-4 focus:ring-pink-500/10 group-hover:border-pink-200"
                />
              </div>

              <div className="group">
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-2 ml-1">
                  {t("lastName")} <span className="text-pink-500">*</span>
                </label>
                <input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Enter last name"
                  disabled={signupMutation.isPending}
                  className="w-full h-12 rounded-xl border border-gray-200 bg-gray-50/50 px-4 text-gray-900 placeholder-gray-400 transition-all duration-300 focus:bg-white focus:border-pink-500 focus:outline-none focus:ring-4 focus:ring-pink-500/10 group-hover:border-pink-200"
                />
              </div>

              {/* Terms & Conditions Checkbox */}
              <div className="flex items-start gap-3 mt-6 p-3 rounded-xl bg-gray-50/50 border border-transparent hover:border-pink-100 transition-colors">
                <div className="relative flex items-center mt-0.5">
                  <input
                    id="terms"
                    type="checkbox"
                    checked={agree}
                    onChange={(e) => setAgree(e.target.checked)}
                    className="peer h-5 w-5 cursor-pointer appearance-none rounded-md border-2 border-gray-300 transition-all checked:border-pink-600 checked:bg-pink-600 hover:border-pink-400"
                  />
                  <svg
                    className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-white opacity-0 peer-checked:opacity-100 transition-opacity"
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M10 3L4.5 8.5L2 6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <label htmlFor="terms" className="text-xs text-gray-600 leading-relaxed cursor-pointer select-none">
                  {t("I agree to the")}{" "}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowTerms(true);
                    }}
                    className="text-pink-600 font-bold hover:text-pink-700 hover:underline transition-colors"
                  >
                    {t("termsAndConditions")}
                  </button>
                </label>
              </div>

              <button
                type="submit"
                disabled={signupMutation.isPending}
                className="w-full h-12 mt-4 rounded-full bg-gradient-to-r from-pink-600 to-rose-600 text-white text-sm font-bold tracking-wide shadow-lg shadow-pink-500/30 hover:shadow-pink-500/50 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {signupMutation.isPending ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    {t("Creating Account...")}
                  </span>
                ) : (
                  t("register")
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  )
}
